import { COHORT_N_LIMITS, COHORT_SCREEN_NAME, COHORT_METHODS, CohortMethod, DEFAULT_COHORT_RULE, DEFAULT_FIXED_RATE, NEWER_COHORTS, OLDER_COHORTS, RECENT_N_LIMITS, defaultRuleFor, describeCohortRule } from './cohort-forecast';
import { AUTO_FALLBACK_RULE, AUTO_MIN_MONTHS, FULL_YEAR_TEST_MONTHS, FULL_YEAR_WINDOW, SEASON_MIN_MONTHS, TREND_MIN_MONTHS, autoCandidates } from './cohort-auto';
import { CONSTANT_GUIDE, GUARD_FLAG_RULE, GUARD_LABEL, PARAM_GUIDE, ageGroupsOf, formatConstant } from './cohort-guard-guide';
import { GUARD_STRUCTURE, MethodConstantKey } from './cohort-guard-defaults';
import { guardHelp } from './cohort-guard-help';
import { COHORT_GUARD_ENGINE_VERSION, LOCAL_SPAN, OWN_HISTORY_MONTHS_RANGE, paramsForEngine, usesRetentionChange, usesSeasonRatio } from './cohort-guards';
import { BUILTIN_FUNCTIONS, INPUT_SOURCES, MODEL_FUNCTIONS, ROLLUP_FUNCTIONS, TOTALS_MODES } from './driver-calc';
import { TREND_MAX, TREND_MIN, describeMixRule } from './mix-forecast';
import { CPS_MISSING_NOTE, DEFAULT_CPS_RULE, DEFAULT_SEEDED_SESSION_RULE, PEAK_NOTE, SESSION_METHODS, SOURCE_METHOD_LABEL, SPEND_DRIVEN_SOURCES, describeSourceRule } from './source-forecast';

export const MODEL_GUIDE_SHEET = 'Model guide';

export type GuideStyle = 'title' | 'section' | 'sub' | 'text' | 'head' | 'cell';

export interface GuideRow {
  style: GuideStyle;
  cells: string[];
}

export interface GuideDriver {
  name: string;
  key: string;
  unit: string;
  section: string;
  src: string;
  formula?: string | null;
  fallback?: string | null;
  rollup?: string | null;
  fill?: string | null;
  levers?: string[];
  min?: number | null;
  max?: number | null;
  totalsMode: string;
  totalsFormula?: string | null;
}

export interface GuideSource {
  name: string;
  key: string;
  manual: boolean;
  sessions: string;
  cps: string;
}

export interface GuideCategory {
  name: string;
  manual: boolean;
  mix: string;
  cogs: string;
  monthRules: number;
}

export interface GuideChannel {
  label: string;
  model: string;
  blocks: string[];
  members: string[];
  drivers: GuideDriver[];
  sources: GuideSource[];
  categories: GuideCategory[];
}

export interface GuideContext {
  version: string;
  versionType: string;
  exported: string;
  cutoff: string;
  actualThrough: string;
  period: string;
  scenario: string;
  currency: string;
  fyStart: string;
  engine: string | null;
  constants: Record<MethodConstantKey, number>;
  forecastRules: boolean;
  channels: GuideChannel[];
}

const SOURCE_TYPE_TEXT: Record<string, string> = {
  input: 'Actual months come from the client data. Forecast months are typed by the planner; an empty forecast month uses the fallback formula.',
  binding: 'Actual months are copied from the client data. Forecast months use the fallback formula, if any.',
  snowflake: 'Actual months are copied from the client data. Forecast months use the fallback formula, if any.',
  fitted: 'The model estimates it from other drivers, for example CAC = ad spend ÷ new customers.',
  formula: 'Worked out from other drivers with the formula shown, in every month.',
  cohort: 'A formula that calls the cohort model (COHORT_ORDERS or BASE_ORDERS).',
  mix: 'A formula that calls the product mix (PRODUCT_COGS_PCT).',
  source: 'A formula that reads the marketing sources (SOURCES or SOURCE_RATIO).',
  rollup: 'Combined from the channels it rolls up, with the rollup rule shown.'
};

const FUNCTION_TEXT: Record<string, string> = {
  SUM: 'Adds its arguments, skipping empty ones. Empty only when every argument is empty.',
  COALESCE: 'The first argument that has a value.',
  IF: 'IF(test, a, b): a when the test is non-zero, otherwise b.',
  MIN: 'Smallest argument. Empty when any argument is empty.',
  MAX: 'Largest argument. Empty when any argument is empty.',
  AVERAGE: 'Average of the arguments that have a value.',
  ABS: 'Absolute value.',
  ACROSS: 'Reads its last argument; used for values shared across dimensions.',
  ACROSS_COHORTS: 'Reads its first argument; used for values shared across cohorts.',
  NULL: 'Always empty.',
  SCENARIO: 'SCENARIO("lever") = 1 + the scenario setting for that lever in forecast months, and 1 in actual months.',
  SOURCES: 'SOURCES("ad_spend") or SOURCES("sessions"): the sum over the channel\'s marketing sources. SOURCES(field, "manual") sums manual sources only.',
  SOURCE_RATIO: 'SOURCE_RATIO(a, b): in forecast months, the ratio of the source totals a ÷ b in the latest actual month where b is above zero. Empty in actual months.',
  COHORT_ORDERS: 'COHORT_ORDERS("block", entrants): orders in each month from the cohort model of that block (see the cohort model section).',
  BASE_ORDERS: 'BASE_ORDERS("block", entrants, "lever"): orders(t) = orders(t−1) × carry + entrants(t), where carry is fitted to actuals and kept between 0.01 and 0.99.',
  PRODUCT_COGS_PCT: 'PRODUCT_COGS_PCT(gross sales): the blended product COGS % from the product mix (see the product mix section).'
};

const TOTALS_TEXT: Record<string, string> = {
  add_up: 'Add up: channel values and month values are summed.',
  formula: 'Formula: the totals formula is worked out again from the summed parts, so ratios and percentages are never averaged or summed.',
  no_totals: 'No totals: left blank in All channels and in quarter and year totals.'
};

const ROLLUP_TEXT: Record<string, string> = {
  SUM: 'SUM: adds the member channels.',
  AVG: 'AVG: simple average of the member channels.',
  WAVG: 'WAVG: average of the member channels weighted by a second driver, written WAVG(x, w).',
  MIN: 'MIN: smallest member channel value.',
  MAX: 'MAX: largest member channel value.'
};

const METHOD_TEXT: Record<CohortMethod, string> = {
  prev: 'Copies the rate one earlier cohort had at the same age, n months back.',
  avg: 'Simple average of the last n cohorts at the same age, using actual results only.',
  wavg: 'Average of the last n cohorts at the same age, weighted by cohort size, using actual results only.',
  chain: 'Starts from the cohort\'s own latest actual rate and applies the typical month to month decay of recent cohorts at each age.',
  seasonal: 'Same age a year earlier, scaled by the recent year on year change. Older saved rules move to the weighted average with seasonality and trend on.',
  fixed: 'One fixed repeat rate for every forecast month.'
};

const SERIES_TEXT: Record<CohortMethod, string> = {
  prev: 'value(t) = value(t − n). n = 12 gives the same month last year.',
  avg: 'value(t) = simple average of the n months before t.',
  wavg: 'value(t) = average of the n months before t, weighted by the weight series (spend for sources, gross sales for product mix).',
  chain: 'value(t) = value(t − 1) × (sum of the last n actual months ÷ sum of the months just before each of them).',
  seasonal: 'value(t) = value(t − 12) × trend, where trend = sum of the last n months ÷ the same n months a year earlier, kept between ' + TREND_MIN + ' and ' + TREND_MAX + '.',
  fixed: 'value(t) = the typed value.'
};

const pct = (v: number) => String(Math.round(v * 1000) / 10) + '%';
const range = ([lo, hi]: [number, number]) => lo + ' to ' + hi;
const text = (line: string): GuideRow => ({ style: 'text', cells: [line] });
const sub = (line: string): GuideRow => ({ style: 'sub', cells: [line] });
const blank = (): GuideRow => ({ style: 'text', cells: [] });
const head = (cells: string[]): GuideRow => ({ style: 'head', cells });
const cell = (cells: string[]): GuideRow => ({ style: 'cell', cells });

function section(n: number, title: string): GuideRow[] {
  return [blank(), { style: 'section', cells: [n + ' · ' + title] }];
}

function bullets(lines: string[]): GuideRow[] {
  return lines.map(l => text('• ' + l));
}

function ruleText(d: GuideDriver): string {
  const parts: string[] = [];
  if (d.src === 'rollup') parts.push(d.rollup ? 'Rollup ' + d.rollup : d.formula || 'Σ channels');
  else if (INPUT_SOURCES.indexOf(d.src) >= 0) {
    parts.push(d.src === 'fitted' ? 'Fitted by the model' : d.src === 'input' ? 'Actuals from data · forecast typed' : 'Actuals from data');
    if (d.fill === 'carry') parts.push('empty forecast months carry the last value');
    if (d.fallback) parts.push('if empty: ' + d.fallback);
    if (d.rollup) parts.push('combined channels: ' + d.rollup);
  } else parts.push(d.formula || '');
  if (d.levers && d.levers.length) parts.push('scenario lever ' + d.levers.join(', '));
  if (typeof d.min === 'number' || typeof d.max === 'number') parts.push('kept between ' + (typeof d.min === 'number' ? d.min : '−∞') + ' and ' + (typeof d.max === 'number' ? d.max : '∞'));
  return parts.filter(Boolean).join(' · ');
}

function totalsText(d: GuideDriver): string {
  if (d.totalsMode === 'formula') return 'Formula' + (d.totalsFormula ? ' · ' + d.totalsFormula : '');
  return d.totalsMode === 'no_totals' ? 'No totals' : 'Add up';
}

function reviewerRows(): GuideRow[] {
  return [
    ...section(1, 'Instructions for the reviewer'),
    text('You are reviewing a growth model forecast. This sheet explains exactly how the product calculates every number; the other sheets hold the numbers.'),
    text('Goal: confirm the forecast is built correctly and the approach is sound, and report only material and fundamental problems.'),
    sub('Report'),
    ...bullets([
      'A step in the chain that does not follow the rules or formulas in this guide (for example orders that do not equal cohort size × repeat rate).',
      'Broken links: a driver that reads the wrong driver, a total that does not reconcile with its parts, a block missing from a total.',
      'Double counting or missing items in revenue, discounts, returns, COGS, fees or marketing.',
      'Unit errors: percentages treated as whole numbers, money treated as counts, ratios summed across months or channels.',
      'Impossible or implausible values: negative orders, sessions or spend; repeat rates above 100% outside the birth month; mix shares far from 100% in total; cost per session of zero.',
      'A forecast that ignores the method stated for it, or a step change at the cutoff that the method cannot explain.',
      'Red cells or safety ceiling hits that change the result materially and have no reason recorded on the Flag review sheet.'
    ]),
    sub('Do not report'),
    ...bullets([
      'Normal forecast variance, or differences of opinion about the future that the stated method allows.',
      'Rounding differences: values are stored to 4–6 decimal places and sessions are whole numbers.',
      'The known behaviours listed in section 11.'
    ]),
    sub('For each issue give'),
    ...bullets([
      'Sheet, driver name and driver_key, and the months affected.',
      'What this guide says should happen, what the workbook shows, and the size of the difference in money or orders.',
      'Why it matters and the most likely cause.'
    ])
  ];
}

function workbookRows(ctx: GuideContext): GuideRow[] {
  return [
    ...section(2, 'How the workbook is laid out'),
    ...bullets([
      'One sheet per channel: every driver row by month. Columns: Driver, driver_key, Unit, then months.',
      'Actual months (up to the cutoff) are shaded and hold the client\'s actual values. Forecast months are not shaded.',
      'Forecast cells carry Excel formulas wherever the step can be written in Excel, so editing an input or a cohort rate recalculates the model. Steps Excel cannot express (marketing sources, product mix, SOURCE_RATIO, typed inputs) are written as values from the app.',
      'Cohort sheets (one per cohort block): cohort size, the method used, the repeat rate grid, and Orders in month = SUMPRODUCT(cohort size, repeat rate) + the bundle row.',
      'Product mix sheet: mix %, gross sales by category, COGS %, COGS £ and the blended product COGS %.',
      'Sources sheet: sessions, ad spend and cost per session for each marketing source.',
      'All channels: the combined view. Quarterly or Annual: period totals.',
      'Guardrails: every cohort rule, its value and source, and every red cell. Flag review: who accepted each red cell and why.',
      'Meta: version, cutoff, scenario, forecast methods, engine version and export checks.',
      'Percentages are stored as fractions: 0.25 means 25%. Money is in the client\'s reporting currency (' + ctx.currency + ').'
    ])
  ];
}

function structureRows(ctx: GuideContext): GuideRow[] {
  return [
    ...section(3, 'Model structure'),
    ...bullets([
      'Channel: a sales channel with its own drivers, cohorts, product mix and marketing sources. Combined channels add up member channels; custom channels can reference other channels.',
      'Block: a cohort model inside a channel, for example subscription or one time purchase. Each block has its own cohorts and settings.',
      'Months: the model runs monthly. The cutoff is the last actual month used by this version (' + ctx.cutoff + '); later months are forecast. The latest month of loaded actuals is ' + ctx.actualThrough + '.',
      'Actuals are loaded from the client\'s data warehouse: driver values, cohort orders, product mix and marketing sources. Manual drivers, sources and categories are saved in the app and add + or − adjustments.',
      'Drivers are defined per channel by an admin: name, driver_key, unit, section, how it is calculated, formula and totals rule. Nothing about the chain is fixed in code; the live definitions for this export are in section 12.',
      'Versions: the draft is the working forecast. A published version is frozen with its inputs, rules and engine version. Financial year starts in ' + ctx.fyStart + '.',
      'Scenarios multiply forecast months only, never actual months.'
    ]),
    sub('How each driver is calculated'),
    head(['Calculated by', 'Meaning']),
    ...Object.keys(SOURCE_TYPE_TEXT).map(k => cell([k.toUpperCase(), SOURCE_TYPE_TEXT[k]])),
    sub('Order of a forecast month for a typed driver'),
    ...bullets([
      'Typed value × its scenario lever.',
      'If no value is typed and the driver carries forward, the last actual value × its scenario lever.',
      'Otherwise the fallback formula.',
      'Finally kept between the driver\'s minimum and maximum, if set.',
      'In actual months an overwrite formula can replace the loaded value, for example to remove a one-off.'
    ]),
    sub('Formula rules'),
    ...bullets([
      'Drivers are worked out in dependency order. Drivers in a circular reference are left empty and flagged.',
      'Any calculation with an empty input is empty, and division by zero is empty, so gaps are visible rather than shown as zero.',
      'Formulas reference other drivers by driver_key; a channel prefix reads another channel.'
    ]),
    head(['Function', 'What it does']),
    ...BUILTIN_FUNCTIONS.concat(MODEL_FUNCTIONS).map(f => cell([f, FUNCTION_TEXT[f] || ''])),
    sub('Totals'),
    ...TOTALS_MODES.map(m => text('• ' + (TOTALS_TEXT[m] || m))),
    text('• Combined channels use a rollup rule per driver: ' + ROLLUP_FUNCTIONS.map(f => ROLLUP_TEXT[f] || f).join(' ')),
    text('• Quarter and year totals follow the same rule: add up sums the months; formula works the totals formula out from the period sums.')
  ];
}

function seriesMethodRows(): GuideRow[] {
  return [
    head(['Method', 'Calculation']),
    ...COHORT_METHODS.map(m => cell([SOURCE_METHOD_LABEL[m], SERIES_TEXT[m]])),
    text('Each forecast month is built from the months before it, including earlier forecast months. If a method has too little history, the last known value is used.')
  ];
}

function sourceRows(): GuideRow[] {
  return [
    ...section(4, 'Source model: ad spend, sessions and cost per session'),
    ...bullets([
      'Each channel can have marketing sources (for example paid search, affiliates, organic). Each source has ad spend, sessions and cost per session.',
      'Actual months: cost per session = ad spend ÷ sessions.',
      'Ad spend is typed per source for forecast months, and multiplied by the source\'s scenario lever.',
      'Sessions follow one rule per source: ' + SESSION_METHODS.map(m => SOURCE_METHOD_LABEL[m]).join(', ') + '.',
      describeSourceRule({ method: 'spend' }) + ': sessions = round(ad spend ÷ cost per session). Cost per session is typed or forecast with its own rule, by default ' + describeSourceRule(DEFAULT_CPS_RULE) + '.',
      describeSourceRule({ method: 'direct' }) + '.',
      'Any other method forecasts sessions directly from their history, weighted by ad spend, rounded to whole sessions.',
      'New sources start as spend driven when their key ends in ' + SPEND_DRIVEN_SOURCES.join(' or ') + ', otherwise ' + describeSourceRule(DEFAULT_SEEDED_SESSION_RULE) + '.',
      'Warning: ' + PEAK_NOTE,
      'Warning: ' + CPS_MISSING_NOTE,
      'Channel drivers read the sources through SOURCES(...) and SOURCE_RATIO(...).'
    ]),
    sub('Forecast methods for sources and product mix'),
    ...seriesMethodRows()
  ];
}

function acquisitionRows(): GuideRow[] {
  return [
    ...section(5, 'From spend to new customers and cohort entrants'),
    text('The exact formulas are the client\'s driver definitions in section 12. A typical chain is:'),
    ...bullets([
      'Ad spend = SOURCES("ad_spend"); sessions = SOURCES("sessions") or ad spend × SOURCE_RATIO("sessions", "ad_spend").',
      'New customers = sessions × conversion rate.',
      'New customer orders = new customers × first orders per new customer.',
      'Entrants per block = new customer orders × the block\'s share (for example subscription share and one time purchase share).',
      'Entrants become the size of each forecast cohort in its birth month.',
      'KPIs such as CAC = marketing spend ÷ new customers and ROAS = sales ÷ ad spend are formulas on the same rows.'
    ])
  ];
}

function methodsTable(): GuideRow[] {
  return [
    head(['Method', 'n allowed', 'n default', 'Calculation']),
    ...COHORT_METHODS.map(m => {
      const rule = defaultRuleFor(m);
      return cell([
        COHORT_SCREEN_NAME[m],
        m === 'fixed' ? 'Rate 0% to 100%' : range(COHORT_N_LIMITS[m as Exclude<CohortMethod, 'fixed'>]),
        m === 'fixed' ? pct(DEFAULT_FIXED_RATE) : String(rule.n),
        METHOD_TEXT[m]
      ]);
    })
  ];
}

function autoRows(engine: string | null): GuideRow[] {
  const seen: Record<string, number[]> = {};
  autoCandidates(FULL_YEAR_TEST_MONTHS, !usesRetentionChange(engine)).forEach(r => {
    if (!seen[r.method]) seen[r.method] = [];
    if (typeof r.n === 'number' && seen[r.method].indexOf(r.n) < 0) seen[r.method].push(r.n);
  });
  return [
    sub('Auto'),
    ...bullets([
      'When no method is set for a block, Auto picks it with a back test: it forecasts the latest held-out months from the data before them and keeps the option with the smallest error.',
      'Options tested: ' + Object.keys(seen).map(m => COHORT_SCREEN_NAME[m as CohortMethod] + ' (n ' + seen[m].sort((a, b) => a - b).join(', ') + ')').join('; ') + ', each with and without monthly seasonality' + (usesRetentionChange(engine) ? '' : ' and year on year trend') + ' where history allows.',
      'Accuracy = 1 − average of |forecast orders − actual orders| ÷ actual orders across the held-out months.',
      'Held-out months: ' + FULL_YEAR_WINDOW + ' with at least ' + FULL_YEAR_TEST_MONTHS + ' months of actuals, otherwise the Auto window constant.',
      'Needs ' + AUTO_MIN_MONTHS + ' months of actuals. With less, Auto uses ' + describeCohortRule(AUTO_FALLBACK_RULE) + (', with monthly seasonality from ' + SEASON_MIN_MONTHS + ' months.'),
      'Monthly seasonality needs ' + SEASON_MIN_MONTHS + ' months of actuals; year on year trend needs ' + TREND_MIN_MONTHS + '.',
      'Without Auto or a block rule the default is ' + describeCohortRule(DEFAULT_COHORT_RULE) + '.'
    ])
  ];
}

function cohortRows(ctx: GuideContext): GuideRow[] {
  const engine = ctx.engine || COHORT_GUARD_ENGINE_VERSION;
  const v4 = usesSeasonRatio(engine);
  const help = (key: string) => guardHelp(key, ctx.constants, v4);
  const groups = ageGroupsOf(GUARD_STRUCTURE.trendBands);
  const rule = (title: string, key: string) => {
    const h = help(key);
    return h ? [cell([title, h.what + ' ' + h.how])] : [];
  };
  return [
    ...section(6, 'Cohort model: repeat orders'),
    text('Engine ' + engine + (engine === COHORT_GUARD_ENGINE_VERSION ? ' (current).' : ' (frozen with this version; the current engine is ' + COHORT_GUARD_ENGINE_VERSION + ').')),
    sub('Definitions'),
    ...bullets([
      'Cohort: the customers whose first order fell in a month. Cohort size = first month orders (entrants).',
      'Repeat rate at month t = orders the cohort placed in month t ÷ cohort size. The birth month is 100% by definition.',
      'Age = months since the birth month. Rates are compared across cohorts at the same age.',
      'Orders in month t = Σ over cohorts (cohort size × repeat rate at t) + bundle orders.',
      'Actual months use actual rates. Forecast cohorts take their size from the entrants driver in their birth month.',
      'A typed rate always wins. Later rule-based months of that cohort follow on from it.',
      'The block\'s orders row multiplies COHORT_ORDERS by its scenario lever in forecast months.'
    ]),
    sub('Forecast method'),
    ...(v4
      ? bullets([
        NEWER_COHORTS + ': use the method chosen for the block (or Auto).',
        OLDER_COHORTS + ': once a cohort has the threshold number of actual months it continues from its own history. The threshold is tuned by back test from ' + range(OWN_HISTORY_MONTHS_RANGE) + ' months, or set by the user.',
        'A method set on a single cohort always wins.',
        'Monthly seasonality and year on year trend are switches on the method.'
      ])
      : bullets(['Each block has one method; a method set on a single cohort wins.'])),
    ...methodsTable(),
    text('The block method list offers n from ' + range(RECENT_N_LIMITS) + ' for the recent cohort methods.'),
    ...autoRows(engine),
    sub('Rules applied to every cohort forecast'),
    head(['Rule', 'What it does and how']),
    cell([GUARD_LABEL.anchors, 'Forecasts learn from actual results only, never from other forecast cells, so errors cannot compound.']),
    ...rule(GUARD_LABEL.legacy, 'legacy'),
    ...rule(GUARD_LABEL.credible, 'credible'),
    ...rule('Monthly seasonality', 'season'),
    ...(v4 ? [cell(['Month factor window', 'Each month is compared with the ' + LOCAL_SPAN + ' months either side of it.'])] : []),
    ...rule(GUARD_LABEL.own, 'own'),
    ...rule(GUARD_LABEL.tail, 'tail'),
    ...rule(GUARD_LABEL.retention, 'retentionChange'),
    ...rule(GUARD_LABEL.cap, 'cap'),
    ...rule(GUARD_LABEL.qa, 'qa'),
    text('Age groups used by the ceiling: ' + groups.map(g => g.name + ' (' + g.months + ')').join(', ') + '.'),
    text('Red cell reasons: ' + (Object.keys(GUARD_FLAG_RULE) as Array<keyof typeof GUARD_FLAG_RULE>).map(k => k + ' ' + GUARD_FLAG_RULE[k]).join('; ') + '.'),
    sub('Settings and how their defaults are set'),
    head(['Setting', 'parameter_key', 'Default and meaning']),
    ...paramsForEngine(engine).map(p => {
      const h = help(p);
      return cell([PARAM_GUIDE[p].label, p, h ? h.how : '']);
    }),
    text('The values used for each channel and block, and whether each was set by a user, are on the Guardrails and Meta sheets.'),
    sub('Method constants (admin)'),
    head(['Constant', 'key', 'Value']),
    ...(Object.keys(CONSTANT_GUIDE) as MethodConstantKey[]).map(k => cell([CONSTANT_GUIDE[k].label, k, formatConstant(k, ctx.constants[k])])),
    sub('Base model channels'),
    text('Channels on the base model use BASE_ORDERS instead of cohorts: orders(t) = orders(t − 1) × carry + entrants(t), with carry fitted to the actual months by least squares.')
  ];
}

function mixRows(): GuideRow[] {
  return [
    ...section(7, 'Product mix and COGS'),
    ...bullets([
      'Mix % per category = the category\'s share of gross sales (excluding discounts and tax).',
      'COGS % per category = product cost ÷ the category\'s gross sales.',
      'Blended product COGS % = Σ (mix % × COGS %). Product COGS = gross sales × blended product COGS %.',
      'Mix % and COGS % are each forecast per category with the methods in section 4, weighted by gross sales, from the rule set for the channel or category. Month-specific rules and typed values override single months.',
      'Mix % is never below 0; a category with no value in a month other categories have is 0. COGS % may be negative for adjustments.',
      'Mix shares are not forced to add to 100%. The Mix total row shows the sum and should be close to 100%.',
      'Without a rule the default is ' + describeMixRule(DEFAULT_COHORT_RULE) + '.'
    ])
  ];
}

function chainRows(): GuideRow[] {
  return [
    ...section(8, 'Main model: from spend to profit'),
    text('The client\'s driver definitions in section 12 are the source of truth. A typical chain is:'),
    ...bullets([
      'Acquisition: ad spend → sessions → new customers → entrants (section 5).',
      'Orders per block: COHORT_ORDERS or BASE_ORDERS × scenario lever (section 6).',
      'Sales per block = orders × average order value. Total gross sales = Σ blocks.',
      'Net sales = gross sales − discounts − returns + shipping revenue, each usually a % of gross sales.',
      'Product COGS = gross sales × PRODUCT_COGS_PCT(gross sales) (section 7).',
      'Fulfilment, payment fees and other variable costs: per order or % of sales.',
      'Marketing = ad spend plus any other marketing costs.',
      'Contribution or profit = net sales − product COGS − fulfilment − fees − marketing − any other costs the client defines. The bottom line row is whatever the client\'s definitions end with.',
      'KPIs (CAC, ROAS, AOV, margin %) are formulas on the rows above and use the Formula totals rule.'
    ])
  ];
}

function checkRows(): GuideRow[] {
  return [
    ...section(9, 'Checks a reviewer can run'),
    ...bullets([
      'Actual months on every sheet match the client\'s data; the forecast starts the month after the cutoff.',
      'Cohort sheets: birth month = 100%; Orders in month = SUMPRODUCT(cohort size, rate) + bundle; the channel\'s block orders = that row × scenario lever.',
      'Forecast cohort sizes equal the entrants driver in the birth month.',
      'Forecast repeat rates at each age stay within the range of recent actual rates at that age, unless a typed rate or retention change explains it.',
      'Sources: sessions = round(ad spend ÷ cost per session) for spend driven sources; sessions total = Σ sources; ad spend total = the channel\'s ad spend.',
      'Product mix: Mix total ≈ 100%; category gross = gross × mix %; COGS £ = category gross × COGS %; blended % = COGS £ ÷ gross.',
      'Every formula row in section 12 recalculates to the value shown.',
      'All channels: add up rows = Σ channels; formula rows = the totals formula on the summed parts.',
      'Quarter and year: add up rows = Σ months; ratio rows are recomputed, never summed.',
      'A scenario changes forecast months only.',
      'Meta lists export_check cells: formula cells with no value in the app.'
    ])
  ];
}

function sanityRows(): GuideRow[] {
  return [
    ...section(10, 'Signs of a material problem'),
    ...bullets([
      'A large jump or drop in orders, sales or COGS in the first forecast month that no input explains.',
      'Repeat rates rising with age, or forecast rates above the best actual result at the same age.',
      'Cost per session falling while spend rises above its historical peak.',
      'Mix total far from 100%, or a blended COGS % outside the historical range.',
      'Profit growing faster than sales for many months without a cost driver that explains it.',
      'Any total that does not equal the sum of its parts.'
    ])
  ];
}

function knownRows(): GuideRow[] {
  return [
    ...section(11, 'Known behaviours that are not errors'),
    ...bullets([
      'No currency conversion: every channel is in the reporting currency; the symbol is for display only.',
      'Mix shares are not normalised to 100% (see section 7).',
      'Drivers with No totals are blank in All channels and in quarter and year totals.',
      'Cohort rates, sources, product mix and typed inputs are values in Excel; the rows that depend on them are formulas.',
      'In Excel, SCENARIO(...) is written as 1. On a scenario export, cells holding values already include the scenario.',
      'Seasonality factors are 1 with under 12 months of actuals, so early forecasts can look flat.',
      'Small cohorts below the learning minimum still get a forecast but do not shape others.',
      'Early cohorts may be grouped into one bundle row (Pre month) and forecast as a whole.',
      'Published versions keep their engine version and settings, even after the engine is updated.',
      'Typed values and accepted red cells are deliberate planner decisions; review them on Flag review rather than treating them as calculation errors.'
    ])
  ];
}

function liveRows(ctx: GuideContext): GuideRow[] {
  const rows: GuideRow[] = [
    ...section(12, 'Live configuration in this export'),
    text('Version ' + ctx.version + ' (' + ctx.versionType + ') · exported ' + ctx.exported + ' · period ' + ctx.period + ' · cutoff ' + ctx.cutoff + ' · scenario ' + ctx.scenario + '.')
  ];
  ctx.channels.forEach(ch => {
    rows.push(blank(), sub(ch.label + ' · ' + ch.model + (ch.blocks.length ? ' · blocks: ' + ch.blocks.join(', ') : '') + (ch.members.length ? ' · combines: ' + ch.members.join(', ') : '')));
    if (ch.drivers.length) {
      rows.push(head(['Driver', 'driver_key', 'Unit', 'Section', 'Calculated by', 'Totals', 'Formula or rule']));
      ch.drivers.forEach(d => rows.push(cell([d.name, d.key, d.unit, d.section, d.src.toUpperCase(), totalsText(d), ruleText(d)])));
    }
    if (ch.sources.length) {
      rows.push(head(['Marketing source', 'source_key', 'Sessions rule', 'Cost per session rule']));
      ch.sources.forEach(s => rows.push(cell([s.name + (s.manual ? ' (manual)' : ''), s.key, s.sessions, s.cps])));
    }
    if (ch.categories.length) {
      rows.push(head(['Product category', 'Mix % rule', 'COGS % rule', 'Month rules']));
      ch.categories.forEach(c => rows.push(cell([c.name + (c.manual ? ' (manual)' : ''), ctx.forecastRules ? c.mix : 'No forecast', ctx.forecastRules ? c.cogs : 'No forecast', String(c.monthRules)])));
    }
  });
  return rows;
}

export function buildModelGuide(ctx: GuideContext): GuideRow[] {
  return [
    { style: 'title', cells: ['Model guide · how every number in this workbook is calculated'] },
    text('Generated from the live model at export, so it always matches the version and settings exported. Engine ' + (ctx.engine || COHORT_GUARD_ENGINE_VERSION) + '.'),
    ...reviewerRows(),
    ...workbookRows(ctx),
    ...structureRows(ctx),
    ...sourceRows(),
    ...acquisitionRows(),
    ...cohortRows(ctx),
    ...mixRows(),
    ...chainRows(),
    ...checkRows(),
    ...sanityRows(),
    ...knownRows(),
    ...liveRows(ctx)
  ];
}
